import { withRecommendedLimits } from "./counterparty-limits.mjs";

export function counterpartyTaskProblem(task, journal) {
  if (
    task?.solutionId !== "solution_counterparty_research_v2" ||
    task.status !== "failed" ||
    !["identity_unavailable", "identity_conflict"].includes(journal?.diagnostic)
  )
    return null;
  return {
    ...preparationProblem({ code: journal.diagnostic }, journal),
    stageLabel: "Подготовка компании",
  };
}

export function preparationSourceOutcomes(diagnostic, journal) {
  const sources = journal?.sources || diagnostic?.sources || [];
  const documents = sources.filter(
    (source) => source.id !== `s-${source.sourceRegistryId}`,
  );
  const rows = documents.map((source) => ({
    id: source.id,
    title: source.title,
    url: source.url,
    description:
      source.accessStatus === "retrieved"
        ? "Страница получена; реквизиты компании не подтверждены."
        : source.accessNote || "Не удалось получить страницу.",
  }));
  for (const search of journal?.searches || []) {
    if (documents.some((source) => source.sourceRegistryId === search.sourceId))
      continue;
    const source = sources.find(
      (source) => source.sourceRegistryId === search.sourceId,
    );
    rows.push({
      id: `search-${search.sourceId}`,
      title: source?.title || search.sourceId,
      description:
        search.status !== "completed"
          ? "Поиск не завершён."
          : search.urls?.length
            ? "Найдены ссылки; страницы не получены."
            : "Поиск не вернул ссылок на документы.",
    });
  }
  return rows;
}

export function preparationProblem(diagnostic, journal) {
  if (diagnostic.code === "identity_conflict")
    return {
      title: "Нужно уточнить компанию",
      description: "Источники дают несовместимые сочетания ИНН и ОГРН.",
    };
  const stopped = {
    budget_limit: [
      "Подготовка остановлена по лимиту расходов",
      "Перед платным обращением нужен резерв в пределах лимита. Подтверждённые расходы могут быть меньше удержанного резерва.",
    ],
    time_limit: [
      "Истекло время подготовки",
      "Не удалось подтвердить компанию за отведённое время.",
    ],
    resource_limit: [
      "Достигнут предел поиска или документов",
      "Не удалось подтвердить компанию в пределах заданных ограничений.",
    ],
    pricing_unavailable: [
      "Поиск остановлен: тариф не подтверждён",
      "Не удалось проверить верхнюю границу стоимости следующего обращения.",
    ],
  }[journal?.stopReason];
  if (stopped) return { title: stopped[0], description: stopped[1] };
  if (journal?.documentCount === 0)
    return {
      title: "Не удалось получить страницы компании",
      description:
        "Поиск не дал материалов для подтверждения реквизитов. Это не означает, что ИНН указан неверно или компания не существует.",
    };
  return {
    title: "Не удалось подтвердить компанию",
    description:
      "Не удалось сопоставить наименование, ИНН и ОГРН по полученным материалам.",
  };
}

export function prepareCounterpartyDraft(draft, settings) {
  let next = withRecommendedLimits(draft, settings.recommendedLimits);
  for (const [key, role] of [["independentResearcher", "researcher_2"], ["analyst", "analyst_3"], ["factChecker", "factchecker_4"]]) {
    if (next[key] && !String(next[key].prompt || "").trim() && settings.recommendedAgentPrompts?.[role])
      next = { ...next, [key]: { ...next[key], prompt: settings.recommendedAgentPrompts[role] } };
  }
  if (String(next.prompt || "").trim() || !settings.recommendedPrompt)
    return next;
  return { ...next, prompt: settings.recommendedPrompt };
}

export function sourceStateLabel(state) {
  return (
    {
      ready: "Готово",
      searchable: "Поиск в интернете",
      requires_access: "Требуется доступ",
      manual_only: "Загруженный документ",
      unavailable: "Не подключён",
      disabled: "Отключён",
    }[state] || "Состояние неизвестно"
  );
}

export function sourceReadyForResearch(source) {
  return (
    source.enabled &&
    ((source.method === "public_page" &&
      ["ready", "searchable"].includes(source.state)) ||
      (source.method === "uploaded_document" &&
        source.state === "manual_only" &&
        Boolean(source.uploadedText)))
  );
}

export function enablePublicSourceSearch(sources, recommended) {
  const publicSources = (recommended || []).filter(
    (source) =>
      source.method === "public_page" &&
      source.state === "searchable" &&
      !source.credentialRef,
  );
  const publicIds = new Set(publicSources.map((source) => source.sourceId));
  const existingIds = new Set(sources.map((source) => source.sourceId));
  const updated = sources.map((source) =>
    publicIds.has(source.sourceId) &&
    source.method === "public_page" &&
    source.state !== "requires_access" &&
    !source.credentialRef
      ? { ...source, enabled: true, state: "searchable" }
      : source,
  );
  return [
    ...updated,
    ...publicSources
      .filter((source) => !existingIds.has(source.sourceId))
      .map((source) => ({
        ...source,
        enabled: true,
        domains: [...source.domains],
        paths: [...source.paths],
        categories: [...source.categories],
      })),
  ];
}
